import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) { clearTimeout(closeTimeoutRef.current); closeTimeoutRef.current = null; }
    setIsDropdownOpen(true);
  };
  const handleMouseLeave = () => { closeTimeoutRef.current = setTimeout(() => { setIsDropdownOpen(false); }, 200); };
  useEffect(() => { return () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); }; }, []);

  useEffect(() => {
    if (isMobileMenuOpen) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-[72px]">
            <Link to="/" className="flex items-center gap-3 shrink-0">
              <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-grid-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-xl font-bold text-foreground-950 whitespace-nowrap hidden sm:block">Superbuy Spreadsheet Hub</span>
              <span className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap sm:hidden">SS Hub</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <div ref={dropdownRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <button className="flex items-center gap-1.5 text-sm font-label font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
                  Categories<i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 border border-background-200 rounded-xl py-2 shadow-lg">
                    <div className="max-h-[420px] overflow-y-auto">
                      {categories.map((cat) => (
                        <a key={cat.name} href={cat.url} target="_blank" rel="noopener nofollow" className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
                          <span className="w-7 h-7 flex items-center justify-center"><i className={`${cat.icon} text-base text-primary-500`}></i></span>{cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Link to="/articles/superbuy-spreadsheet-guide" className="text-sm font-label font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">Complete Guide</Link>
              <Link to="/articles/superbuy-spreadsheet-finds" className="text-sm font-label font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">Best Finds</Link>
              <Link to="/articles/superbuy-beginner-guide" className="text-sm font-label font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">Beginner</Link>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm font-label font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">Main Store</a>
            </div>

            <div className="flex items-center gap-3">
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="hidden sm:inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-label font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                Buy It Now<i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer" aria-label="Toggle menu">
                <i className={`text-xl text-foreground-700 ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Fullscreen Overlay Menu */}
      <div className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-background-50"></div>

        <div className="relative z-10 h-full overflow-y-auto pt-[72px] pb-8">
          <div className="px-4">

            {/* Featured Quick Access Cards */}
            <div className="mb-8">
              <p className="text-[10px] font-label font-semibold text-foreground-400 uppercase tracking-[0.15em] mb-3">Quick Access</p>
              <div className="grid grid-cols-3 gap-3">
                <Link to="/articles/superbuy-spreadsheet-guide" onClick={() => setIsMobileMenuOpen(false)} className="bg-background-100 rounded-xl p-4 text-center hover:bg-background-200/70 transition-colors cursor-pointer">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-primary-100 flex items-center justify-center">
                    <i className="ri-book-open-line text-lg text-primary-500"></i>
                  </div>
                  <span className="text-[11px] font-label font-semibold text-foreground-800 whitespace-nowrap">Guide</span>
                </Link>
                <Link to="/articles/superbuy-spreadsheet-finds" onClick={() => setIsMobileMenuOpen(false)} className="bg-background-100 rounded-xl p-4 text-center hover:bg-background-200/70 transition-colors cursor-pointer">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-accent-100/70 flex items-center justify-center">
                    <i className="ri-search-eye-line text-lg text-accent-600"></i>
                  </div>
                  <span className="text-[11px] font-label font-semibold text-foreground-800 whitespace-nowrap">Finds</span>
                </Link>
                <Link to="/articles/superbuy-beginner-guide" onClick={() => setIsMobileMenuOpen(false)} className="bg-background-100 rounded-xl p-4 text-center hover:bg-background-200/70 transition-colors cursor-pointer">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-secondary-100 flex items-center justify-center">
                    <i className="ri-user-smile-line text-lg text-secondary-600"></i>
                  </div>
                  <span className="text-[11px] font-label font-semibold text-foreground-800 whitespace-nowrap">Beginner</span>
                </Link>
              </div>
            </div>

            {/* All Categories */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-label font-semibold text-foreground-400 uppercase tracking-[0.15em]">All Categories</p>
                <span className="text-[10px] font-label text-foreground-300">{categories.length} categories</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a key={cat.name} href={cat.url} target="_blank" rel="noopener nofollow" onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2.5 p-3 bg-background-100 rounded-xl hover:bg-background-200/60 transition-colors cursor-pointer group">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-50 text-primary-500 group-hover:bg-primary-50 transition-colors">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    <div className="min-w-0">
                      <span className="text-xs font-label font-semibold text-foreground-800 block truncate">{cat.name}</span>
                      <span className="text-[10px] text-foreground-400">Browse &rarr;</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* More Articles */}
            <div className="mb-8">
              <p className="text-[10px] font-label font-semibold text-foreground-400 uppercase tracking-[0.15em] mb-3">More Guides</p>
              <div className="space-y-1.5">
                {[
                  { to: '/articles/superbuy-spreadsheet-links', icon: 'ri-link', label: 'Spreadsheet Links Guide' },
                  { to: '/articles/superbuy-qc-guide', icon: 'ri-magic-line', label: 'QC Spreadsheet Guide' },
                  { to: '/articles/superbuy-spreadsheet-shoes', icon: 'ri-footprint-line', label: 'Shoes Discovery Guide' },
                  { to: '/articles/superbuy-spreadsheet-hoodies', icon: 'ri-t-shirt-line', label: 'Hoodies & Sweaters Guide' },
                  { to: '/articles/superbuy-spreadsheet-jackets', icon: 'ri-contrast-drop-2-line', label: 'Jackets & Outerwear Guide' },
                  { to: '/articles/superbuy-spreadsheet-safety', icon: 'ri-shield-check-line', label: 'Safety Guide' },
                  { to: '/articles/superbuy-shipping-guide', icon: 'ri-ship-line', label: 'Shipping Guide' },
                ].map((item) => (
                  <Link key={item.to} to={item.to} onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                      <i className={`${item.icon} text-sm`}></i>
                    </span>
                    <span className="text-sm text-foreground-700 font-label font-medium">{item.label}</span>
                    <i className="ri-arrow-right-s-line text-foreground-300 ml-auto"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="pt-4 border-t border-background-200">
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow"
                className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-primary-500 text-background-50 text-sm font-label font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer mb-3">
                <i className="ri-shopping-bag-line text-base"></i>
                Buy It Now
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow"
                className="w-full flex items-center justify-center gap-2 px-5 py-3.5 border-2 border-foreground-200 text-foreground-700 text-sm font-label font-semibold rounded-full hover:border-foreground-400 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-external-link-line text-base"></i>
                Visit Main Store
              </a>
              <p className="text-center text-[10px] text-foreground-300 mt-4">
                Independent Resource &bull; Updated 2026
              </p>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}